import React, { ComponentProps, useState } from 'react'

import { ResponsiveImageWithSkeletonWrapper } from '@syconium/little-miss-figgy'

import { ContentfulRichText } from '../../../../../../../../app/_components/pages/contentful/components/ContentfulRichText.client'
import { RenderOnceInViewport } from '../../../../../../../../app/_components/performance/RenderOnceInViewport.client'
import { useTranslation } from '../../../../../../../../app/_providers/TranslationProvider.client'
import { MaybeSpaLink } from '../../../../../../../../lib/adapters/next-routing-service/MaybeSpaLink'
import { trackEvent } from '../../../../../../../../lib/analytics'
import { useResponsiveVideoSrc } from '../../../../../../../../lib/hooks/useResponsiveVideoSrc'
import { EditorialCell as Props } from '../../../../../../../../types/graphql'
import { InlineVideoPlatforms } from '../../../../../../../../types/video'
import { VideoModal } from '../../../../../../VideoModal'
import { VimeoVideo } from '../../../../../../VimeoVideo'
import { EditorialCellDescriptionPosition } from '../types'

import EditorialCellDescription from './components/EditorialCellDescription'
import {
  Container,
  Cta,
  Disclaimer,
  MediaWrapper,
  TextWrapper,
  Tile,
  Video,
  VideoWrapper,
} from './styles'

const EditorialCellImage: React.FC<{
  alt: string
  aspectRatios: ComponentProps<typeof ResponsiveImageWithSkeletonWrapper>['aspectRatios']
  image: string | null
  imageMobile: string | null
  loading: 'eager' | 'lazy' | undefined
  fetchPriority: 'high' | 'low' | 'auto' | undefined
}> = ({ alt, aspectRatios, image, imageMobile, loading, fetchPriority }) => {
  if (!image) return null

  const imageComponent = (
    <>
      <ResponsiveImageWithSkeletonWrapper
        alt={alt}
        loading={loading === 'eager' ? 'default' : 'lazy'}
        fetchPriority={fetchPriority}
        src={image}
        srcs={{
          sm: imageMobile ?? image,
          md: image,
        }}
        aspectRatios={aspectRatios}
        widths={{
          unit: 'vw',
          sm: 100,
          md: 100,
        }}
      />
    </>
  )

  return imageComponent
}

export const EditorialCell: React.FC<
  Props & { isCriticalCell: boolean; cellWidth?: number | null }
> = ({
  backgroundColor,
  ctaText,
  ctaAlignment,
  disclaimer,
  description,
  image: imageDesktop,
  imageAspectRatio: _imageDesktopAspectRatio,
  imageMobile,
  imageMobileAspectRatio,
  inlineVideo,
  inlineVimeoId,
  inlineVimeoIdMobile,
  linkHref,
  modalVideoId,
  modalVideoIdMobile,
  modalVideoPlatform,
  subtitle,
  textColor,
  title,
  descriptionSize,
  descriptionPlacement: requestedDescriptionPlacement,
  descriptionPadding,
  descriptionColor,
  descriptionAlignment,
  descriptionByLine,
  reviewStars,
  titleAlignment,
  titleSize,
  isCriticalCell,
  cellWidth,
  eyebrow,
}) => {
  const {
    magnolia: { general },
  } = useTranslation()

  // We have to cast this because type gen types it as a string, and to update it to an enum
  // we would need to migrate away from how its values contain spaces.
  const descriptionPlacement = requestedDescriptionPlacement as
    | keyof typeof EditorialCellDescriptionPosition
    | undefined
    | null

  const hasVideo = !!(modalVideoPlatform && (modalVideoId || modalVideoIdMobile))
  const [isModalOpen, setIsModalOpen] = useState(false)

  const getTrackEventProps = () =>
    trackEvent({
      category: 'editorial cell',
      action: 'click editorial cell',
      value: ctaText ?? '',
    })

  const onClick = (e: React.MouseEvent) => {
    if (hasVideo) {
      e.preventDefault()
      setIsModalOpen(true)
    }
  }

  const video = useResponsiveVideoSrc({
    inlineVideoDesktop: inlineVideo,
    inlineVideoMobile: inlineVideo,
    inlineVimeoIdDesktop: inlineVimeoId,
    inlineVimeoIdMobile: inlineVimeoIdMobile ?? inlineVimeoId,
  })

  const renderInlineVideo = () => {
    if (video?.platform !== InlineVideoPlatforms.DEFAULT_PLAYER) return

    const videoComponent = <Video autoPlay loop muted playsInline src={video.src} />

    return <RenderOnceInViewport>{videoComponent}</RenderOnceInViewport>
  }

  const showModalCta = !linkHref && ctaText && hasVideo

  const isNotTextOnly =
    descriptionPlacement &&
    EditorialCellDescriptionPosition[descriptionPlacement] !==
      EditorialCellDescriptionPosition['Text only (40px padding top and bottom)']

  const nonMediaContentOverlaysMedia = descriptionPlacement
    ? EditorialCellDescriptionPosition[descriptionPlacement] ===
      EditorialCellDescriptionPosition.Bottom
    : false

  const isContentBeforeMedia = descriptionPlacement
    ? EditorialCellDescriptionPosition[descriptionPlacement] ===
      EditorialCellDescriptionPosition['Above image']
    : false

  const hasNonMediaContent = description || title || subtitle || (linkHref && ctaText)

  const getAspectRatios = () => {
    const portraitAspectRatio = 20 / 26
    const tallPortraitAspectRatio = 20 / 33

    const landscapeAspectRatio = 21 / 18
    const wideLandscapeAspectRatio = 16 / 9

    let desktopAspectRatio: number = portraitAspectRatio

    // Defaults to 1 cell width
    if (cellWidth === undefined || cellWidth === 1) {
      if (hasNonMediaContent && !nonMediaContentOverlaysMedia) {
        desktopAspectRatio = portraitAspectRatio
      } else {
        desktopAspectRatio = tallPortraitAspectRatio
      }
    } else if (cellWidth === 2) {
      desktopAspectRatio = landscapeAspectRatio
    } else {
      desktopAspectRatio = wideLandscapeAspectRatio
    }

    let mobileAspectRatio =
      imageMobileAspectRatio && imageMobileAspectRatio > portraitAspectRatio
        ? imageMobileAspectRatio
        : cellWidth === undefined || cellWidth === 1
        ? desktopAspectRatio
        : portraitAspectRatio

    return {
      sm: mobileAspectRatio,
      md: desktopAspectRatio,
    }
  }

  const renderTileBody = () => {
    return (
      <>
        {!video && (
          <EditorialCellImage
            alt={title || description || ''}
            aspectRatios={getAspectRatios()}
            image={imageDesktop}
            imageMobile={imageMobile}
            loading={isCriticalCell ? 'eager' : 'lazy'}
            fetchPriority={isCriticalCell ? 'high' : undefined}
          />
        )}
        {video?.platform === InlineVideoPlatforms.DEFAULT_PLAYER && (
          <VideoWrapper>{renderInlineVideo()}</VideoWrapper>
        )}
        {video?.platform === InlineVideoPlatforms.VIMEO && (
          <VimeoVideo
            playText={general.playVideo}
            pauseText={general.pauseVideo}
            position='relative'
            autoplay
            isInteractive={false}
            lazy
            thumbnail={
              <EditorialCellImage
                alt={title || description || ''}
                aspectRatios={getAspectRatios()}
                image={imageDesktop}
                imageMobile={imageMobile}
                loading={isCriticalCell ? 'eager' : 'lazy'}
                fetchPriority={isCriticalCell ? 'high' : undefined}
              />
            }
            videoSrc={video.src}
          />
        )}
        {hasNonMediaContent && nonMediaContentOverlaysMedia ? (
          <TextWrapper isFloating>
            <EditorialCellDescription
              subtitle={subtitle ?? undefined}
              title={title}
              titleSize={titleSize}
              titleAlignment={titleAlignment}
              textColor={textColor}
              description={description}
              descriptionPadding={descriptionPadding}
              descriptionAlignment={descriptionAlignment}
              descriptionByLine={descriptionByLine}
              reviewStars={reviewStars}
              descriptionSize={descriptionSize}
              descriptionColor={descriptionColor}
              ctaText={ctaText}
              eyebrow={eyebrow}
              isFloating={true}
            />
          </TextWrapper>
        ) : null}
      </>
    )
  }

  return (
    <Container
      isInverted={isContentBeforeMedia}
      isFloating={nonMediaContentOverlaysMedia || !hasNonMediaContent}
    >
      {(!descriptionPlacement || isNotTextOnly) && (video || imageDesktop) && (
        <MediaWrapper cellWidth={cellWidth}>
          <Tile
            {...{
              backgroundColor,
              onClick,
              hasBottomPadding: !(title || description || subtitle),
            }}
          >
            {linkHref ? (
              <MaybeSpaLink href={linkHref} {...getTrackEventProps()}>
                {renderTileBody()}
              </MaybeSpaLink>
            ) : (
              <>{renderTileBody()}</>
            )}
            <Disclaimer>
              <ContentfulRichText
                content={disclaimer}
                bodySize='BodyMicro'
                mobileBodySize='BodyNano'
                fontWeight='bold'
              />
            </Disclaimer>
          </Tile>
          {hasVideo && (
            <VideoModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              videoPlatform={modalVideoPlatform}
              videoId={modalVideoId}
              videoIdMobile={modalVideoIdMobile}
            />
          )}
        </MediaWrapper>
      )}
      {hasNonMediaContent && !nonMediaContentOverlaysMedia ? (
        <TextWrapper
          isTextOnly={!isNotTextOnly}
          placement={descriptionPlacement}
          textAlignment={ctaAlignment}
        >
          <EditorialCellDescription
            subtitle={subtitle}
            title={title}
            titleSize={titleSize}
            titleAlignment={titleAlignment}
            textColor={textColor}
            description={description}
            descriptionPadding={descriptionPadding}
            descriptionAlignment={descriptionAlignment}
            descriptionByLine={descriptionByLine}
            descriptionColor={descriptionColor}
            reviewStars={reviewStars}
            showModalCta={showModalCta}
            descriptionSize={descriptionSize}
            linkHref={linkHref}
            ctaText={ctaText}
            eyebrow={eyebrow}
          />
          {showModalCta && <Cta {...{ onClick }}>{ctaText}</Cta>}
        </TextWrapper>
      ) : null}
    </Container>
  )
}
